import type { Blocker, PartialAct, Result, RowTask, TaskBox } from "@/components/ui/box.schema";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";

export type SessionTaskKind = "prepare" | "close";

/** What a host reports for one session's Prepare or Close: its read, then its Task once read. */
export type SessionRowReport =
  | { status: "loading" }
  | { status: "failed"; retry: () => void }
  /** The session's read was refused (401, 403 or 404): nothing it held is drawn or sent (R1, CASE-018). */
  | { status: "refused" }
  /** `stale`: the last read is kept, and a refresh failed; the row says when it was read and offers Try again. */
  | { status: "ready"; workspace: SessionWorkspace; box: TaskBox<unknown> | null; stale?: { readAt: string; retry: () => void } };

/**
 * A Task's parts as a row's, under the row's key: a part's id is unique in its
 * List, so two sessions' Close parts never share an id, and each blocker,
 * effect and failed line still links to its own part (F5).
 */
export function rowTaskOf(box: TaskBox<unknown>, key: string): RowTask {
  const id = (part: string) => `${key}:${part}`;
  const blocker = (item: Blocker): Blocker => ("part" in item && item.part ? { ...item, part: id(item.part) } as Blocker : item);
  const again = (act: PartialAct): PartialAct => ({ ...act, run: async (requestId) => result(await act.run(requestId)) });
  const result = (value: Result): Result => {
    if (value.state === "unknown") return { ...value, check: { ...value.check, run: async () => result(await value.check.run()) } };
    if (value.state !== "partial") return value;
    const lines = {
      went: value.went.map((line) => (line.fix ? { ...line, fix: id(line.fix) } : line)),
      failed: value.failed.map((line) => (line.fix ? { ...line, fix: id(line.fix) } : line)),
    };
    // A partial with unsure ids carries Check, and Retry only beside it.
    return value.check
      ? { ...value, ...lines, check: again(value.check), retry: value.retry && again(value.retry) }
      : { ...value, ...lines, retry: again(value.retry) };
  };
  const { commit } = box;
  return {
    parts: box.parts.map((part) => ({ ...part, id: id(part.id) })),
    commit: {
      ...commit,
      effects: () => commit.effects().map((effect) => (effect.part ? { ...effect, part: id(effect.part) } : effect)),
      blockers: () => commit.blockers().map(blocker),
      revises: commit.revises ? { ...commit.revises, part: id(commit.revises.part) } : undefined,
      run: async (requestId) => result(await commit.run(requestId)),
    },
    result: result(box.result),
  };
}

/**
 * A row's Task that reads its List again after each answer that may have
 * changed the session, from its commit, a partial's retry or a Check: the
 * row's own mark then follows what the Task did (K3, R1).
 */
export function readingAfter(task: RowTask, read: () => void): RowTask {
  const after = (result: Result): Result => {
    if (result.state === "confirmed" || result.state === "partial") read();
    return wrap(result);
  };
  const again = (act: PartialAct): PartialAct => ({ ...act, run: async (requestId) => after(await act.run(requestId)) });
  const wrap = (result: Result): Result => {
    if (result.state === "partial") {
      return result.check
        ? { ...result, check: again(result.check), retry: result.retry && again(result.retry) }
        : { ...result, retry: again(result.retry) };
    }
    if (result.state === "unknown") return { ...result, check: { ...result.check, run: async () => after(await result.check.run()) } };
    return result;
  };
  return { ...task, commit: { ...task.commit, run: async (requestId) => after(await task.commit.run(requestId)) }, result: wrap(task.result) };
}
